import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '@/layouts/organization-layout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Globe,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Lock,
  Loader2,
  Copy,
  Check,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { csrfHeaders } from '@/lib/csrf'
import type { DnsRecord, DomainWithDns } from '@/types/dns'

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA', 'PTR'] as const

interface DomainsPageProps {
  domains: DomainWithDns[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
}

function Index({ domains }: DomainsPageProps) {
  const { t } = useTranslation('organizations')

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground">{t('domains.title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('domains.description')}
              </p>
            </div>

            {domains.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
                <Globe className="h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-4 text-lg font-medium">{t('domains.emptyState.title')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('domains.emptyState.description')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {domains.map((domain) => (
                  <DomainCard key={domain.id} domain={domain} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>
export default Index

function DomainCard({ domain }: { domain: DomainWithDns }) {
  const { t } = useTranslation('organizations')
  const [expanded, setExpanded] = useState(false)
  const [records, setRecords] = useState<DnsRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRecords = useCallback(async (force = false) => {
    if (loaded && !force) return
    setLoaded(false)
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/organization/domains/${domain.id}/dns_records`, {
        headers: { ...csrfHeaders(), 'Accept': 'application/json' },
      })
      const data = await response.json()

      if (data.success) {
        setRecords(data.records)
        setLoaded(true)
      } else {
        setError(data.error || t('domains.errors.loadFailed'))
      }
    } catch {
      setError(t('domains.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [domain.id, loaded, t])

  const handleToggle = () => {
    const next = !expanded
    setExpanded(next)
    if (next && !loaded) {
      fetchRecords()
    }
  }

  const statusBadge = () => {
    switch (domain.activation_status) {
      case 'active':
        return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">{t('domains.status.active')}</Badge>
      case 'configuring_dns':
      case 'activating_certificate':
        return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400">{t('domains.status.settingUp')}</Badge>
      case 'failed':
        return <Badge variant="destructive">{t('domains.status.failed')}</Badge>
      default:
        return <Badge variant="secondary">{t('domains.status.pending')}</Badge>
    }
  }

  return (
    <div className="rounded-lg border bg-card">
      <button
        className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Globe className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{domain.domain_name}</span>
              {statusBadge()}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {domain.project_name}
              {domain.expires_at && (
                <> &middot; {t('domains.expiresAt', { date: new Date(domain.expires_at).toLocaleDateString() })}</>
              )}
            </p>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 mt-4">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {loaded && !loading && (
            <DnsRecordsTable
              domainId={domain.id}
              domainName={domain.domain_name}
              records={records}
              onRecordChanged={() => fetchRecords(true)}
            />
          )}
        </div>
      )}
    </div>
  )
}

function DnsRecordsTable({
  domainId,
  domainName,
  records,
  onRecordChanged,
}: {
  domainId: number
  domainName: string
  records: DnsRecord[]
  onRecordChanged: () => void
}) {
  const { t } = useTranslation('organizations')
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DnsRecord | null>(null)
  const [deletingRecord, setDeletingRecord] = useState<DnsRecord | null>(null)

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          {t('domains.records.title')}
        </h4>
        <Button size="sm" onClick={() => setShowAddDialog(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          {t('domains.records.addRecord')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">{t('domains.records.columns.type')}</th>
              <th className="text-left p-3 font-medium">{t('domains.records.columns.name')}</th>
              <th className="text-left p-3 font-medium">{t('domains.records.columns.value')}</th>
              <th className="text-left p-3 font-medium w-20">{t('domains.records.columns.ttl')}</th>
              <th className="text-right p-3 font-medium w-24">{t('domains.records.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {records.map((record, idx) => (
              <DnsRecordRow
                key={`${record.type}-${record.name}-${idx}`}
                record={record}
                onEdit={() => setEditingRecord(record)}
                onDelete={() => setDeletingRecord(record)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <RecordFormDialog
        key={showAddDialog ? 'add-open' : 'add-closed'}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        domainId={domainId}
        domainName={domainName}
        onSuccess={onRecordChanged}
      />

      {editingRecord && (
        <RecordFormDialog
          key={`edit-${editingRecord.type}-${editingRecord.name}`}
          open={true}
          onOpenChange={(open) => { if (!open) setEditingRecord(null) }}
          domainId={domainId}
          domainName={domainName}
          record={editingRecord}
          onSuccess={onRecordChanged}
        />
      )}

      {deletingRecord && (
        <DeleteRecordDialog
          open={true}
          onOpenChange={(open) => { if (!open) setDeletingRecord(null) }}
          domainId={domainId}
          record={deletingRecord}
          onSuccess={onRecordChanged}
        />
      )}
    </div>
  )
}

function DnsRecordRow({
  record,
  onEdit,
  onDelete,
}: {
  record: DnsRecord
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('organizations')
  const [copiedValue, setCopiedValue] = useState<string | null>(null)

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
    setCopiedValue(value)
    toast.success(t('domains.records.copied'))
    setTimeout(() => setCopiedValue(null), 2000)
  }

  const typeBadgeColor = () => {
    switch (record.type) {
      case 'A':
      case 'AAAA':
        return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400'
      case 'CNAME':
        return 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-400'
      case 'MX':
        return 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-400'
      case 'TXT':
        return 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400'
      default:
        return 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400'
    }
  }

  return (
    <tr className={`hover:bg-muted/30 transition-colors ${record.system_managed ? 'opacity-70' : ''}`}>
      <td className="p-3">
        <Badge variant="outline" className={typeBadgeColor()}>
          {record.type}
        </Badge>
      </td>
      <td className="p-3 font-mono text-xs max-w-[200px] truncate">
        {record.name}
      </td>
      <td className="p-3 max-w-[300px]">
        <div className="space-y-1">
          {record.values.map((value, idx) => (
            <div key={idx} className="flex items-center gap-1 group">
              <span className="font-mono text-xs truncate">{value}</span>
              <button
                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                onClick={() => copyToClipboard(value)}
              >
                {copiedValue === value ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
              </button>
            </div>
          ))}
        </div>
      </td>
      <td className="p-3 text-xs text-muted-foreground">
        {record.ttl > 0 ? record.ttl : '—'}
      </td>
      <td className="p-3 text-right">
        {record.system_managed ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Lock className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('domains.records.systemManaged')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} disabled={record.is_alias}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onDelete} disabled={record.is_alias}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  )
}

function RecordFormDialog({
  open,
  onOpenChange,
  domainId,
  domainName,
  record,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  domainId: number
  domainName: string
  record?: DnsRecord
  onSuccess: () => void
}) {
  const { t } = useTranslation('organizations')
  const isEdit = !!record

  const stripTxtQuotes = (v: string) => v.replace(/^"(.*)"$/, '$1')
  const formatInitialValues = (rec: DnsRecord) =>
    rec.type === 'TXT' ? rec.values.map(stripTxtQuotes).join('\n') : rec.values.join('\n')

  const [type, setType] = useState(record?.type || 'A')
  const [name, setName] = useState(record ? record.name : '')
  const [value, setValue] = useState(record ? formatInitialValues(record) : '')
  const [ttl, setTtl] = useState(String(record?.ttl || 300))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)

    const values = value.split('\n').map((v) => v.trim()).filter(Boolean)

    if (values.length === 0) {
      setError(t('domains.records.errors.valueRequired'))
      setSubmitting(false)
      return
    }

    const method = isEdit ? 'PATCH' : 'POST'

    try {
      const response = await fetch(`/organization/domains/${domainId}/dns_records`, {
        method,
        headers: { ...csrfHeaders(), 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          record: {
            type,
            name: name || '@',
            values,
            ttl: parseInt(ttl, 10) || 300,
          },
        }),
      })
      const data = await response.json()

      if (data.success) {
        toast.success(isEdit ? t('domains.records.toasts.updated') : t('domains.records.toasts.created'))
        onOpenChange(false)
        onSuccess()
      } else {
        setError(data.error || t('domains.records.errors.saveFailed'))
      }
    } catch {
      setError(t('domains.records.errors.saveFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('domains.records.editRecord') : t('domains.records.addRecord')}
          </DialogTitle>
          <DialogDescription>
            {t('domains.records.formDescription', { domain: domainName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('domains.records.columns.type')}</Label>
            <Select value={type} onValueChange={setType} disabled={isEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORD_TYPES.map((rt) => (
                  <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('domains.records.columns.name')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('domains.records.namePlaceholder')}
              disabled={isEdit}
            />
            <p className="text-xs text-muted-foreground">
              {t('domains.records.nameHint', { domain: domainName })}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t('domains.records.columns.value')}</Label>
            <Textarea
              className="min-h-[80px]"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('domains.records.valuePlaceholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('domains.records.valueHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t('domains.records.columns.ttl')}</Label>
            <Input
              type="number"
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
              min={60}
              max={86400}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('domains.records.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('domains.records.saving')}
              </>
            ) : (
              isEdit ? t('domains.records.update') : t('domains.records.create')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteRecordDialog({
  open,
  onOpenChange,
  domainId,
  record,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  domainId: number
  record: DnsRecord
  onSuccess: () => void
}) {
  const { t } = useTranslation('organizations')
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    setDeleting(true)

    try {
      const response = await fetch(`/organization/domains/${domainId}/dns_records`, {
        method: 'DELETE',
        headers: { ...csrfHeaders(), 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          record: {
            type: record.type,
            name: record.name,
            values: record.values,
            ttl: record.ttl,
          },
        }),
      })
      const data = await response.json()

      if (data.success) {
        toast.success(t('domains.records.toasts.deleted'))
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error(data.error || t('domains.records.errors.deleteFailed'))
      }
    } catch {
      toast.error(t('domains.records.errors.deleteFailed'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('domains.records.deleteDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('domains.records.deleteDialog.description', { type: record.type, name: record.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>{t('domains.records.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleDelete}
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('domains.records.deleting')}
              </>
            ) : (
              t('domains.records.deleteConfirm')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
